/**
 * BuildStaleBanner — tells the operator when the page he is looking at is older
 * than the code on the server.
 *
 * See `utils/buildFreshness.ts` for the incident that produced this. Short
 * version: a fix can be built, deployed and verified correct on the server and
 * STILL not be the code running in the tab that is reporting the bug, because a
 * single-page app never reloads on its own. Hours were spent re-verifying layers
 * that were all already correct.
 *
 * It offers the reload; it never takes it. A surprise refresh mid-drawing would
 * destroy unsaved work — a worse bug than the one this prevents.
 */
import { useEffect, useState } from 'react';
import { onBuildStale, watchBuildFreshness } from '@/utils/buildFreshness';

export default function BuildStaleBanner() {
  const [stale, setStale] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    watchBuildFreshness();
    return onBuildStale(() => setStale(true));
  }, []);

  if (!stale || dismissed) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: 99999, display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 14px', borderRadius: 10,
        background: 'rgba(20,20,28,0.96)', color: '#e6e6ef',
        border: '1px solid rgba(234,179,8,0.55)',
        boxShadow: '0 6px 24px rgba(0,0,0,0.45)',
        font: '500 13px/1.35 system-ui, sans-serif',
      }}
    >
      <span aria-hidden>⟳</span>
      <span>
        This page is running an <strong>older build</strong>. Fixes deployed since
        you opened it are not active here.
      </span>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '5px 12px', borderRadius: 7, cursor: 'pointer',
          background: '#eab308', color: '#1a1a20', border: 'none', fontWeight: 700,
        }}
      >
        Reload
      </button>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        style={{
          padding: '5px 9px', borderRadius: 7, cursor: 'pointer',
          background: 'transparent', color: '#9a9aa8',
          border: '1px solid rgba(255,255,255,0.16)',
        }}
      >
        Later
      </button>
    </div>
  );
}
